// ================================
// src/components/signal/MidPanel.tsx — FINAL SAFE (RR/신뢰도 게이지 정상 작동)
// ================================
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Analysis, n, confidence } from "../../utils/chartUtils";

type Props = { analysis: Analysis | null; price: number | null };

const ns = (v: any, dp = 4) => (Number.isFinite(v) ? n(v as number, dp) : "-");
const isNum = (v: any) => Number.isFinite(v);

const STOP_MULT = 1.2;
const TP1_MULT = 1.0;
const TP2_MULT = 2.0;
const BAND_HALF = 0.5;

const MidPanel: React.FC<Props> = ({ analysis, price }) => {
  const { t } = useTranslation("signal");
  const hasData = isNum(price) && !!analysis;

  // === 방향(dir) ===
  const dir = useMemo(() => {
    if (!analysis || !isNum(price)) return "wait";
    if (analysis.dir) return analysis.dir;
    const ema50 = Number(analysis?.ema50) || 0;
    const ema200 = Number(analysis?.ema200) || 0;
    if ((price as number) > ema50 && (price as number) > ema200) return "long";
    if ((price as number) < ema50 && (price as number) < ema200) return "short";
    return "wait";
  }, [analysis?.dir, analysis?.ema50, analysis?.ema200, price]);

  const dirColor =
    dir === "long" ? "#4ade80" : dir === "short" ? "#f87171" : "#9ca3af";

  // === 플랜(plan) ===
  const plan = useMemo(() => {
    if (!analysis || !isNum(price)) return null;
    const A = Number(analysis?.atr) || 0;
    const entry = price as number;
    const band: [number, number] = [entry - BAND_HALF * A, entry + BAND_HALF * A];

    if (dir === "long") {
      return {
        entry,
        stop: entry - STOP_MULT * A,
        tp1: entry + TP1_MULT * A,
        tp2: entry + TP2_MULT * A,
        band,
      };
    }
    if (dir === "short") {
      return {
        entry,
        stop: entry + STOP_MULT * A,
        tp1: entry - TP1_MULT * A,
        tp2: entry - TP2_MULT * A,
        band,
      };
    }
    return { entry, stop: NaN, tp1: NaN, tp2: NaN, band };
  }, [analysis?.atr, price, dir]);

  // === RR / Confidence ===
  let rr1 = 0;
  let rr2 = 0;
  let confPct = 0;

  if (plan && isNum(plan.entry) && isNum(plan.stop)) {
    const entry = Number(plan.entry);
    const stop = Number(plan.stop);
    const tp1 = Number(plan.tp1);
    const tp2 = Number(plan.tp2);

    const risk = Math.max(1e-9, dir === "short" ? stop - entry : entry - stop);
    rr1 = Math.max(0, (dir === "short" ? entry - tp1 : tp1 - entry) / risk);
    rr2 = Math.max(0, (dir === "short" ? entry - tp2 : tp2 - entry) / risk);

    // 🔥 여기서 confidence() 반환값을 안전하게 처리 (숫자 or 객체 모두 대응)
    const confRaw = confidence(analysis?.score ?? 0, rr1) as any;
    confPct =
      typeof confRaw === "number"
        ? confRaw
        : Number.isFinite(confRaw?.pct)
        ? Number(confRaw.pct)
        : 0;
  }

  // === EMA 근접도 ===
  const ema50 = Number(analysis?.ema50) || 0;
  const ema200 = Number(analysis?.ema200) || 0;
  const emaMin = Math.min(ema50, ema200);
  const emaMax = Math.max(ema50, ema200);
  const emaRatio =
    emaMax !== emaMin && isNum(price)
      ? Math.max(
          0,
          Math.min(1, ((price as number) - emaMin) / (emaMax - emaMin))
        )
      : 0.5;

  // === S/R 근접도 + 신뢰도 ===
  const sup = Number(analysis?.sup) || 0;
  const res = Number(analysis?.res) || 0;
  const distToS = isNum(price) ? (price as number) - sup : null;
  const distToR = isNum(price) ? res - (price as number) : null;
  const nearS =
    isNum(distToS) && Math.abs(distToS as number) / (price as number || 1) < 0.003;
  const nearR =
    isNum(distToR) && Math.abs(distToR as number) / (price as number || 1) < 0.003;

  const trust = (cnt?: number) => {
    const v = Number(cnt ?? 0);
    return v >= 3 ? "***" : v === 2 ? "**-" : v === 1 ? "*--" : "---";
  };

  // === 변동성 레이블 (기존 번역키 그대로) ===
  const volLabel =
    analysis?.volReg === "high"
      ? t("vol.high", "High")
      : analysis?.volReg === "low"
      ? t("vol.low", "Low")
      : t("vol.neutral", "Neutral");

  return (
    <div className="card">
      <div className="card-head" style={{ color: dirColor }}>
        {t("mid.title", "Intermediate Analysis")}
      </div>

      {!hasData && <p className="muted">{t("loading", "Loading")}</p>}

      {/* ===== EMA Trend ===== */}
      <section className="card-section">
        <div className="section-head">{t("mid.trend", "Trend (EMA)")}</div>
        <div className="progress-ema">
          <div
            className="bar"
            style={{
              width: `${emaRatio * 100}%`,
              background: dirColor,
            }}
          />
        </div>
        <div className="kv">
          <span className="k">EMA50 / EMA200</span>
          <span className="v">
            {ns(analysis?.ema50)} / {ns(analysis?.ema200)}
          </span>
        </div>
        <div className="hint">
          {t(
            "mid.pb1",
            "Confidence rises when EMA position aligns with the box."
          )}
        </div>
      </section>

      {/* ===== S/R Info ===== */}
      <section className="card-section">
        <div className="section-head">{t("mid.box", "Price Box")}</div>
        <div className="kv">
          <span className="k">S / R</span>
          <span className="v">
            {ns(sup)} ~ {ns(res)}{" "}
            {nearS || nearR ? t("mid.near", "NEAR") : ""}
          </span>
        </div>
        <div className="kv">
          <span className="k">{t("mid.trust", "Trust")}</span>
          <span className="v">
            S:{trust(analysis?.cntS)} R:{trust(analysis?.cntR)}
          </span>
        </div>
        <div className="hint">
          {t(
            "mid.pb2",
            "Variance high → reduce size; low → scale entries."
          )}
        </div>
      </section>

      {/* ===== Plan (ATR 기반) ===== */}
      <section className="card-section">
        <div className="section-head">{t("mid.plan", "Plan (ATR based)")}</div>
        <div className="kv">
          <span className="k">{t("mid.entry", "Entry")}</span>
          <span className="v">{ns(plan?.entry)}</span>
        </div>
        <div className="kv">
          <span className="k">{t("mid.stop", "Stop")}</span>
          <span className="v">{ns(plan?.stop)}</span>
        </div>
        <div className="kv">
          <span className="k">TP1 / TP2</span>
          <span className="v">
            {ns(plan?.tp1)} / {ns(plan?.tp2)}
          </span>
        </div>
        <div className="kv">
          <span className="k">{t("mid.band", "Entry Band")}</span>
          <span className="v">
            {plan?.band?.length === 2
              ? `${ns(plan.band[0])} ~ ${ns(plan.band[1])}`
              : "-"}
          </span>
        </div>
        <div className="hint">
          {t(
            "mid.bandHint",
            "Avoid chasing inside band; confirm bounce/break first."
          )}
        </div>
        <div className="hint">
          {t(
            "mid.pb3",
            "If R/R is low, prefer re-targeting or waiting."
          )}
        </div>
      </section>

      {/* ===== R/R & Confidence ===== */}
      <section className="card-section">
        <div className="section-head">{t("rr.short", "R/R")}</div>

        <div className="rr-item">
          <span className="k">RR1</span>
          <div className="bar-wrap">
            <div
              className="bar"
              style={{
                width: `${Math.min(rr1 * 50, 100)}%`,
                background: dirColor,
              }}
            />
          </div>
          <span className="v">{n(rr1, 2)}</span>
        </div>

        <div className="rr-item">
          <span className="k">RR2</span>
          <div className="bar-wrap">
            <div
              className="bar"
              style={{
                width: `${Math.min(rr2 * 40, 100)}%`,
                background: dirColor,
              }}
            />
          </div>
          <span className="v">{n(rr2, 2)}</span>
        </div>

        <div className="rr-item">
          <span className="k">{t("mid.confidence", "Confidence")}</span>
          <div className="bar-wrap">
            <div
              className="bar"
              style={{
                width: `${Math.min(confPct, 100)}%`,
                background: dirColor,
              }}
            />
          </div>
          <span className="v">{n(confPct, 0)}%</span>
        </div>
      </section>

      <style>{`
.card-section{border:1px solid #2a2f3a;border-radius:12px;background:#10141c;padding:12px;margin-bottom:10px}
.section-head{font-weight:900;color:#cfe1ff;margin-bottom:8px}
.kv{display:flex;justify-content:space-between;margin-bottom:4px}
.k{opacity:.8;font-size:12px}
.v{font-weight:700}
.progress-ema{height:6px;background:#1a1f2b;border-radius:6px;margin:6px 0;position:relative}
.progress-ema .bar{height:100%;border-radius:6px}
.rr-item{display:flex;align-items:center;gap:8px;margin-bottom:6px}
.bar-wrap{flex:1;height:6px;background:#1b2535;border-radius:4px;overflow:hidden}
.bar{height:100%;border-radius:4px}
.hint{opacity:.85;font-size:13px;margin-top:4px}
.card-head{font-weight:900;font-size:15px;margin-bottom:8px}
`}</style>
    </div>
  );
};

export default MidPanel;
